import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as LucideIcons from 'lucide-react'
import { pushTemplatesApi } from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import { normalizeVariable } from '../utils/templateVariableHelpers'
import {
  PageHeader,
  Button,
  LoadingSpinner,
  ErrorMessage,
  Card,
  CardBody,
  CardHeader,
  FormField,
  FormTextarea,
  Badge,
  Modal
} from '../components/common'
import { ConfirmationModal } from '../components/common'

const TITLE_MAX_LENGTH = 200
const BODY_MAX_LENGTH = 500

const ICON_OPTIONS = [
  { value: '', label: 'Default (no icon)' },
  { value: 'megaphone', label: 'Megaphone' },
  { value: 'bell', label: 'Bell' },
  { value: 'package', label: 'Package' },
  { value: 'car', label: 'Car' },
  { value: 'dumbbell', label: 'Dumbbell' },
  { value: 'wrench', label: 'Wrench' },
  { value: 'shield-check', label: 'Shield Check' },
  { value: 'mail', label: 'Mail' },
  { value: 'message-square', label: 'Message' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'clock', label: 'Clock' },
  { value: 'alert-triangle', label: 'Alert' },
  { value: 'info', label: 'Info' },
  { value: 'check-circle-2', label: 'Check Circle' },
  { value: 'x-circle', label: 'X Circle' },
  { value: 'user', label: 'User' },
  { value: 'building-2', label: 'Building' },
  { value: 'key', label: 'Key' },
  { value: 'star', label: 'Star' },
]

const getIconComponent = (name) => {
  if (!name) return null
  const pascalName = name
    .split('-')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join('')
  return LucideIcons[pascalName] || null
}

const EditPushTemplate = () => {
  const { templateType } = useParams()
  const navigate = useNavigate()
  const titleRef = useRef(null)
  const bodyRef = useRef(null)

  const [formData, setFormData] = useState({
    templateType: parseInt(templateType) || 0,
    templateName: '',
    title: '',
    messageBody: '',
    iconName: '',
  })
  const [variables, setVariables] = useState([])
  const [isExisting, setIsExisting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activeField, setActiveField] = useState('title')
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)

  useEffect(() => {
    loadData()
  }, [templateType])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load variables and existing template in parallel
      const [variablesRes, templateRes] = await Promise.allSettled([
        pushTemplatesApi.getVariablesByType(templateType),
        pushTemplatesApi.get(templateType),
      ])

      // Process variables
      if (variablesRes.status === 'fulfilled') {
        const data = variablesRes.value.data
        if (data && Array.isArray(data.variables)) {
          setVariables(data.variables)
        } else if (Array.isArray(data)) {
          setVariables(data)
        } else {
          setVariables([])
        }
      }

      // Process existing template
      if (templateRes.status === 'fulfilled' && templateRes.value.data) {
        const t = templateRes.value.data
        setFormData({
          templateType: parseInt(templateType),
          templateName: t.templateName || '',
          title: t.title || '',
          messageBody: t.messageBody || '',
          iconName: t.iconName || '',
        })
        setIsExisting(true)
      } else {
        setIsExisting(false)
      }
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const insertVariable = (variableName) => {
    const placeholder = `{{${variableName}}}`
    const fieldRef = activeField === 'title' ? titleRef : bodyRef
    const fieldName = activeField === 'title' ? 'title' : 'messageBody'

    if (fieldRef.current) {
      const el = fieldRef.current
      const start = el.selectionStart || 0
      const end = el.selectionEnd || 0
      const currentValue = formData[fieldName]
      const newValue = currentValue.substring(0, start) + placeholder + currentValue.substring(end)

      setFormData(prev => ({ ...prev, [fieldName]: newValue }))

      // Restore cursor position after React re-render
      requestAnimationFrame(() => {
        el.focus()
        const newCursorPos = start + placeholder.length
        el.setSelectionRange(newCursorPos, newCursorPos)
      })
    } else {
      // Fallback: append to end
      setFormData(prev => ({
        ...prev,
        [fieldName]: prev[fieldName] + placeholder,
      }))
    }
  }

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError('Please enter a description for the AI to generate content')
      return
    }

    setAiLoading(true)
    setAiError(null)

    try {
      const response = await pushTemplatesApi.generateAI({
        prompt: aiPrompt,
        templateType: formData.templateType,
        currentTitle: formData.title || undefined,
        currentMessage: formData.messageBody || undefined
      })

      if (response.data) {
        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          messageBody: response.data.messageBody || prev.messageBody
        }))
        setShowAIModal(false)
        setAiPrompt('')
      }
    } catch (err) {
      setAiError(extractErrorMessage(err))
    } finally {
      setAiLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving) return

    try {
      setSaving(true)

      const apiData = {
        templateType: parseInt(templateType),
        templateName: formData.templateName,
        title: formData.title,
        messageBody: formData.messageBody,
        iconName: formData.iconName || null,
      }

      if (isExisting) {
        await pushTemplatesApi.update(apiData)
      } else {
        await pushTemplatesApi.create(apiData)
      }

      navigate('/notifications?tab=push')
    } catch (err) {
      showError(err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (deleting) return

    try {
      setDeleting(true)
      await pushTemplatesApi.delete(templateType)
      navigate('/notifications?tab=push')
    } catch (err) {
      showError(err)
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="flex flex-col h-full space-y-6">
      <PageHeader
        title={isExisting ? 'Edit Push Template' : 'Create Push Template'}
        subtitle="Configure the push notification template for this event type"
        backLink={{
          label: 'Back to Notifications',
          href: '/notifications?tab=push',
        }}
      />

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex gap-6">
          {/* Main Form */}
          <Card className="flex-1 overflow-y-auto">
            <CardBody>
              <form onSubmit={handleSubmit} className="space-y-6">
                <FormField
                  label="Template Name"
                  name="templateName"
                  value={formData.templateName}
                  onChange={handleInputChange}
                  required
                  maxLength={100}
                  placeholder="e.g., Delivery Notification Push"
                />

                {(() => {
                  const SelectedIcon = getIconComponent(formData.iconName)
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Icon
                      </label>
                      <div className="flex items-center gap-3">
                        <select
                          name="iconName"
                          value={formData.iconName}
                          onChange={handleInputChange}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          {ICON_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        {SelectedIcon && (
                          <div className="p-2 rounded-lg bg-gray-100 border border-gray-200">
                            <SelectedIcon size={20} className="text-gray-700" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Optional icon shown in mobile push notifications
                      </p>
                    </div>
                  )
                })()}

                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    type="button"
                    size="small"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setShowAIModal(true)
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    AI Generate
                  </Button>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Title
                    </label>
                    <span className={`text-xs ${formData.title.length > TITLE_MAX_LENGTH ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {formData.title.length}/{TITLE_MAX_LENGTH}
                    </span>
                  </div>
                  <input
                    ref={titleRef}
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    onFocus={() => setActiveField('title')}
                    required
                    maxLength={TITLE_MAX_LENGTH}
                    placeholder="Notification title (e.g., New Delivery for {{RecipientName}})"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Message Body
                    </label>
                    <span className={`text-xs ${formData.messageBody.length > BODY_MAX_LENGTH ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {formData.messageBody.length}/{BODY_MAX_LENGTH}
                    </span>
                  </div>
                  <textarea
                    ref={bodyRef}
                    name="messageBody"
                    value={formData.messageBody}
                    onChange={handleInputChange}
                    onFocus={() => setActiveField('body')}
                    required
                    maxLength={BODY_MAX_LENGTH}
                    rows={6}
                    placeholder="Notification message body..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                  />
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preview
                  </label>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="bg-white rounded-lg shadow-sm p-4 max-w-sm mx-auto border border-gray-100">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                          {(() => {
                            const PreviewIcon = getIconComponent(formData.iconName)
                            return PreviewIcon
                              ? <PreviewIcon size={16} className="text-white" />
                              : <span className="text-white text-xs font-bold">R</span>
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {formData.title || 'Notification Title'}
                          </p>
                          <p className="text-sm text-gray-600 mt-0.5 line-clamp-3">
                            {formData.messageBody || 'Notification message body will appear here...'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">Reziland - now</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    {isExisting && (
                      <Button
                        variant="danger"
                        type="button"
                        size="small"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setShowDeleteConfirm(true)
                        }}
                        disabled={deleting}
                      >
                        Delete Template
                      </Button>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate('/notifications?tab=push')
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      loading={saving}
                      disabled={saving}
                    >
                      {isExisting ? 'Update Template' : 'Create Template'}
                    </Button>
                  </div>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Variables Sidebar */}
          <Card className="w-80 flex-shrink-0">
            <div className="h-full flex flex-col">
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">Variables</h3>
              </CardHeader>
              <CardBody className="flex-1 overflow-y-auto">
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">
                    Inserting into:
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveField('title')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        activeField === 'title'
                          ? 'bg-primary-100 text-primary-700 border border-primary-300'
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      Title
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveField('body')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        activeField === 'body'
                          ? 'bg-primary-100 text-primary-700 border border-primary-300'
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      Body
                    </button>
                  </div>
                </div>

                {variables.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No variables available for this template type.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-3">
                      Click a variable to insert it:
                    </p>
                    {variables.map((variable, index) => {
                      const { name: varName, isRequired, description, type: variableType } = normalizeVariable(variable)

                      return (
                        <div key={index} className="mb-2">
                          <button
                            type="button"
                            onClick={() => insertVariable(varName)}
                            className="w-full text-left p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <code className="text-sm font-mono text-primary-700">
                                {`{{${varName}}}`}
                              </code>
                              <span className="flex items-center gap-1">
                                {isRequired && (
                                  <Badge variant="danger" size="small">Required</Badge>
                                )}
                                {variableType && variableType !== 'Text' && (
                                  <span className="text-[10px] text-blue-500 ml-1 font-normal">{variableType.toLowerCase()}</span>
                                )}
                              </span>
                            </div>
                          </button>
                          {description && (
                            <p className="text-xs text-gray-500 mt-1 ml-2">{description}</p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                <div className="mt-6 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-primary-800 mb-2">Push Tips:</h4>
                  <ul className="text-xs text-primary-700 space-y-1">
                    <li>Keep titles short and attention-grabbing</li>
                    <li>Body is limited to {BODY_MAX_LENGTH} characters</li>
                    <li>Use variables for personalization</li>
                    <li>Preview shows how it will appear on device</li>
                  </ul>
                </div>
              </CardBody>
            </div>
          </Card>
        </div>
      </div>

      {/* AI Generate Modal */}
      {showAIModal && (
        <Modal
          isOpen={showAIModal}
          onClose={() => setShowAIModal(false)}
          title="Generate Push Template with AI"
          size="large"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-4">
            {aiError && (
              <ErrorMessage
                message={aiError}
                onDismiss={() => setAiError(null)}
              />
            )}

            <FormTextarea
              label="Describe the push notification you want"
              name="aiPrompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={4}
              placeholder="E.g., Create a friendly delivery notification that includes the recipient name and building..."
              disabled={aiLoading}
            />

            {formData.title && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Current title:</strong> {formData.title}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  The AI will improve upon your existing content.
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setShowAIModal(false)
                  setAiPrompt('')
                  setAiError(null)
                }}
                disabled={aiLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleAIGenerate()
                }}
                disabled={aiLoading || !aiPrompt.trim()}
                loading={aiLoading}
              >
                Generate
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Push Template"
          message="Are you sure you want to delete this push template? This action cannot be undone."
          confirmLabel="Delete"
          confirmVariant="danger"
          loading={deleting}
        />
      )}
    </div>
  )
}

export default EditPushTemplate
